import { Box } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import type { ReactNode } from 'react';

const orbitSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const orbitSpinReverse = keyframes`
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
`;

const glowBreath = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.01); }
`;

const PRIMARY_GRADIENT = `conic-gradient(
  from 0deg,
  transparent 0%,
  transparent 45%,
  rgba(251, 191, 36, 0.35) 55%,
  rgba(244, 114, 182, 0.55) 65%,
  rgba(192, 132, 252, 0.5) 75%,
  rgba(129, 140, 248, 0.3) 85%,
  transparent 95%,
  transparent 100%
)`;

const SECONDARY_GRADIENT = `conic-gradient(
  from 180deg,
  transparent 0%,
  transparent 60%,
  rgba(244, 114, 182, 0.2) 70%,
  rgba(251, 191, 36, 0.25) 80%,
  transparent 90%,
  transparent 100%
)`;

interface OrbitLayersProps {
  borderRadius: string | number;
  borderWidth: number;
  speed: number;
}

const OrbitLayers = ({ borderRadius, borderWidth, speed }: OrbitLayersProps) => (
  <>
    {/* Glow — blurred halo that breathes */}
    <Box
      position="absolute"
      inset="-6px"
      borderRadius={borderRadius}
      overflow="hidden"
      pointerEvents="none"
      zIndex={0}
      filter="blur(8px)"
      animation={`${glowBreath} 3s ease-in-out infinite`}
    >
      <Box
        position="absolute"
        inset="-50%"
        animation={`${orbitSpin} ${speed}s linear infinite`}
        background={PRIMARY_GRADIENT}
      />
    </Box>
    {/* Sharp border — primary orbit */}
    <Box
      position="absolute"
      inset={`-${borderWidth}px`}
      borderRadius={borderRadius}
      overflow="hidden"
      pointerEvents="none"
      zIndex={0}
    >
      <Box
        position="absolute"
        inset="-50%"
        animation={`${orbitSpin} ${speed}s linear infinite`}
        background={PRIMARY_GRADIENT}
      />
    </Box>
    {/* Secondary layer — counter-rotating for depth */}
    <Box
      position="absolute"
      inset={`-${borderWidth}px`}
      borderRadius={borderRadius}
      overflow="hidden"
      pointerEvents="none"
      zIndex={0}
    >
      <Box
        position="absolute"
        inset="-50%"
        animation={`${orbitSpinReverse} ${speed * 1.7}s linear infinite`}
        background={SECONDARY_GRADIENT}
      />
    </Box>
  </>
);

interface GradientOrbitBorderProps {
  children: ReactNode;
  active?: boolean;
  borderRadius?: string | number;
  borderWidth?: number;
  speed?: number;
}

export const GradientOrbitBorder = ({
  children,
  active = false,
  borderRadius = '2xl',
  borderWidth = 2,
  speed = 3,
}: GradientOrbitBorderProps) => (
  <Box position="relative">
    {active && <OrbitLayers borderRadius={borderRadius} borderWidth={borderWidth} speed={speed} />}
    <Box position="relative" zIndex={1} borderRadius={borderRadius} bg="white">
      {children}
    </Box>
  </Box>
);

interface GradientOrbitOverlayProps {
  active?: boolean;
  borderRadius?: string | number;
  borderWidth?: number;
  speed?: number;
}

export const GradientOrbitOverlay = ({
  active = false,
  borderRadius = '2xl',
  borderWidth = 2,
  speed = 3,
}: GradientOrbitOverlayProps) => {
  if (!active) return null;

  return (
    <>
      <OrbitLayers borderRadius={borderRadius} borderWidth={borderWidth} speed={speed} />
      {/* Inner mask */}
      <Box
        position="absolute"
        inset={0}
        borderRadius={borderRadius}
        bg="white"
        pointerEvents="none"
        zIndex={0}
      />
    </>
  );
};
